import { Fragment, useEffect, useState } from 'react';
import { Mail, Inbox, AlertCircle } from 'lucide-react';
import { api, timeAgo } from '../api.js';
import { UserChip } from '../components/UserChip.jsx';
import { Pagination } from '../components/Pagination.jsx';
import { useGuild } from './GuildLayout.jsx';

const PAGE = 50;

const CONTEXT_META = {
  warn:               { label: 'Warning',           cls: 'warn' },
  kick:               { label: 'Kick',              cls: 'danger' },
  ban:                { label: 'Ban',               cls: 'danger' },
  timeout:            { label: 'Timeout',           cls: 'warn' },
  guild_blacklisted:  { label: 'Guild blacklisted', cls: 'danger' },
  channel_mod_added:  { label: 'Channel-mod added', cls: 'info' },
  unspecified:        { label: 'Other',             cls: 'dim' },
};

export default function BotDms() {
  const { guildId } = useGuild();
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    setData(null);
    api.botDms(guildId, `?limit=${PAGE}&offset=${offset}`).then(setData).catch(err => setData({ error: err.message }));
  }, [guildId, offset]);

  if (!data) return <div className="card"><div className="skel skel-line med" /><div className="skel" style={{ height: 80, marginTop: 12 }} /></div>;
  if (data.error) return <div className="empty">Error: {data.error}</div>;

  return (
    <>
      <h1>Bot DMs</h1>
      <p className="subtitle">{(data.total || 0).toLocaleString()} DM{data.total === 1 ? '' : 's'} sent by SoupOS.</p>

      {!data.dms.length ? (
        <div className="card empty">
          <div className="ico"><Inbox size={32} strokeWidth={1.5} /></div>
          <p>SoupOS hasn't sent any DMs yet.</p>
        </div>
      ) : (<>
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Recipient</th>
                <th>Type</th>
                <th>Triggered by</th>
                <th>Delivered</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.dms.map(dm => {
                const meta = CONTEXT_META[dm.context] || { label: dm.context, cls: 'dim' };
                const open = expanded === dm.id;
                return (
                  <Fragment key={dm.id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(open ? null : dm.id)}>
                      <td title={new Date(dm.created_at * 1000).toLocaleString()}>{timeAgo(dm.created_at)}</td>
                      <td onClick={e => e.stopPropagation()}><UserChip guildId={guildId} userId={dm.recipient_id} size={20} /></td>
                      <td><span className={`badge ${meta.cls}`}>{meta.label}</span></td>
                      <td onClick={e => e.stopPropagation()}>{dm.triggered_by ? <UserChip guildId={guildId} userId={dm.triggered_by} size={16} /> : <span className="dim">—</span>}</td>
                      <td>
                        {dm.success
                          ? <span className="badge success">delivered</span>
                          : <span className="badge danger" title={dm.error || ''}><AlertCircle size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />failed</span>}
                      </td>
                      <td><Mail size={14} strokeWidth={2} style={{ opacity: .6, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} /></td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={6} style={{ background: 'var(--bg-elev-2)', padding: '1rem' }}>
                          {dm.content && (
                            <div style={{ marginBottom: dm.embed_json ? '.75rem' : 0 }}>
                              <div className="dim" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700, marginBottom: 4 }}>Content</div>
                              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{dm.content}</div>
                            </div>
                          )}
                          {dm.embed_json && (
                            <div>
                              <div className="dim" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700, marginBottom: 4 }}>Embed</div>
                              <pre style={{ background: 'var(--bg)', padding: 8, borderRadius: 6, fontSize: 11, maxHeight: 240, overflow: 'auto', margin: 0 }}>
                                {JSON.stringify(JSON.parse(dm.embed_json), null, 2)}
                              </pre>
                            </div>
                          )}
                          {!dm.success && dm.error && (
                            <div style={{ marginTop: '.75rem', color: 'var(--danger)', fontSize: 13 }}>Error: {dm.error}</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination total={data.total || 0} limit={PAGE} offset={offset} onChange={setOffset} />
      </>)}
    </>
  );
}
